<template>
  <v-dialog
    v-model="show"
    max-width="900px"
  >
    <v-card>
      <v-card-title>Request For L&#38;D
        <v-tooltip top color="#424852">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              style="cursor:pointer;"
            >
              mdi-information
            </v-icon>
          </template>
          <span>Once AM is approved, you will need to find this request in your history and upload your payment files.</span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click.prevent="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <v-expansion-panels v-model="firstPanel">
          <v-expansion-panel>
            <v-expansion-panel-header class="ml-0">Plan info</v-expansion-panel-header>
            <v-expansion-panel-content>
              Please, provide a link to a course or other educational activity you would like to take,
              as well as the reason for taking it.
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row>
          <v-col cols="12" lg="3" md="3">
            <v-text-field
              v-model="courseName"
              label="Course name"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="3" md="3">
            <v-text-field
              :rules="[v => /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm
              .test(v)||'Most likely invalid link']"
              v-model="courseLink"
              label="Course link"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="3" md="3">
            <v-select
              :items="reasons"
              label="Reason"
              v-model="computedReason"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="3" md="3">
            <v-text-field
              label="Other"
              v-model="otherReason"
              :disabled="isOtherReasonDisabled"
            ></v-text-field>
          </v-col>
          <v-expansion-panels v-model="secondPanel">
            <v-expansion-panel>
              <v-expansion-panel-header class="ml-0">Other fields</v-expansion-panel-header>
              <v-expansion-panel-content>
                Fill in the compensation amount, select AM, who will consider this request. Once AM is approved, you will need to find this request in your history and upload your payment files.
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-col cols="12" lg="2" md="2">
            <v-text-field
              v-model="computedCompensationAmount"
              class="inputAmount"
              label="Comp. amount"
              type="number"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2" lg="2">
            <v-select
              v-model="currency"
              :items="currencies"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="4" md="4">
            <v-select
              :items="filteredAccountManagers"
              label="L&D Approver"
              item-value="Employee_ID"
              item-text="Employee_Full_Name"
              v-model="accountManager"
            >
              <template v-slot:prepend-item>
                <div class="px-4 py-2">
                  <v-text-field
                    dense
                    hide-details
                    label="Search Approver"
                    v-model="searchAM"
                  ></v-text-field>
                </div>
                <v-divider></v-divider>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" lg="4" md="4">
            <v-textarea
              v-model="comments"
              rows="1"
              label="Comments (optional)"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small :loading="isLoading" :disabled="!isRequestReady || !isRequestAppropriate || isLoading" @click="sendRequest" color="primary">Send</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'LAndDRequest',
  props: {
    value: {
      required: true,
      default: false
    },
    accountManagers: {
      required: true
    },
    requestId: {
      type: Number,
      default: null
    }
  },
  mounted () {
    this.reason = this.otherReason = 'Personal Development Plan'
    if (this.requestId) {
      this.getLAndDRequest(this.requestId)
    }
  },
  data () {
    return {
      isRequestAppropriate: true,
      isLoading: false,
      firstPanel: 0,
      secondPanel: 0,
      courseLink: null,
      courseName: null,
      searchAM: '',
      reasons: [
        'Personal Development Plan',
        'Project/Client request',
        'Your own initiative',
        'Other'
      ],
      reason: '',
      otherReason: null,
      accountManager: null,
      compensationAmount: null,
      comments: null,
      currency: 'USD',
      currencies: [
        'USD',
        'UAH'
      ],
      notificationData: {
        Notification_Type_ID: 1,
        Notification_Title: '',
        Notification_Message: '',
        Notification_Created: undefined,
        Notification_Employee_ID_From: undefined,
        Notification_Employee_ID_To: undefined
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (this.value) {
          this.clearAll()
          this.$emit('getHistory')
          this.isRequestAppropriate = true
        }
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    computedCompensationAmount: {
      get () {
        return this.compensationAmount
      },
      set (val) {
        if (val < 0) {
          this.compensationAmount = -val
        } else {
          this.compensationAmount = val
        }
      }
    },
    filteredAccountManagers () {
      const r = new RegExp(this.searchAM, 'gi')
      return this.accountManagers.filter(item => {
        r.lastIndex = 0
        return r.test(item.Employee_Full_Name)
      })
    },
    computedReason: {
      get () {
        return this.reason
      },
      set (val) {
        this.reason = val
        if (this.reason !== 'Other') this.otherReason = val
        else this.otherReason = ''
      }
    },
    isOtherReasonDisabled () {
      return this.reason !== 'Other'
    },
    isRequestReady () {
      return this.computedCompensationAmount &&
      (this.reason === 'Other' ? this.otherReason : true) &&
      this.accountManager &&
      this.courseLink &&
      this.courseName
    }
  },
  methods: {
    sendRequest () {
      this.isLoading = true
      this.isRequestAppropriate = false

      const isOther = this.reason === 'Other'
      const reason = isOther ? this.otherReason : this.reason
      const headers = { Authorization: 'Bearer ' + this.user.token }

      const requestPayload = {
        Confirmer_ID: this.accountManager,
        Amount_Of_Compensation: this.computedCompensationAmount,
        Link_To_Course: this.courseLink,
        Course_Name: this.courseName,
        Course_Taking_Reason: reason,
        Comments: this.comments,
        Is_USD: this.currency === 'USD'
      }

      if (this.requestId) {
        this.axios.put(`/api/requests/l_and_d_request/${this.requestId}`, requestPayload, { headers })
          .then((res) => {
            this.$store.dispatch('setSuccess', { successMsg: 'Request details were updated.' })

            return this.axios.delete(`/api/notifications/${res.data[0].Notification_ID}`, { headers })
          })
          .then(() => {
            return this.createNotification()
          })
          .then((newNotificationResponse) => {
            const updatePayload = {
              Notification_ID: newNotificationResponse.data[0].Notification_ID
            }

            return this.axios.put(`/api/requests/update_notification_l_and_d_request/${this.requestId}`, updatePayload, { headers })
          })
          .then(() => {
            this.sendEmailForLAndDRequest(this.requestId)
            this.$store.dispatch('setSuccess', { successMsg: 'Request was fully updated with the new notification.' })
            this.show = false
          })
          .catch(err => {
            this.isRequestAppropriate = true
            const errorMsg = getApiErrorMessage(err)
            this.$store.dispatch('setError', { errorMsg })
          }).finally(() => {
            this.isLoading = false
          })
      } else {
        this.createNotification()
          .then(response => {
            this.postLAndDRequest(response.data[0].Notification_ID)
          })
          .catch(error => {
            this.isRequestAppropriate = true
            this.$store.dispatch('setError', {
              errorMsg: 'Failed to create notification: ' + error.message
            })
          }).finally(() => {
            this.isLoading = false
          })
      }
    },
    getLAndDRequest (requestId) {
      this.axios.get(`/api/requests/l_and_d_by_id/${requestId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const data = res.data[0]
        this.accountManager = data.Confirmer_ID
        this.courseLink = data.Link_To_Course
        this.courseName = data.Course_Name
        this.otherReason = data.Course_Taking_Reason
        if (!this.reasons.includes(data.Course_Taking_Reason)) {
          this.reason = 'Other'
        } else {
          this.reason = data.Course_Taking_Reason
        }
        this.compensationAmount = data.Amount_Of_Compensation
        this.comments = data.Comments
        this.currency = data.Is_USD ? 'USD' : 'UAH'
      }).catch(err => {
        const errorMsg = getApiErrorMessage(err)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    postLAndDRequest (notificationId) {
      this.axios.post('/api/requests/l_and_d',
        {
          Creator_ID: this.user.employee_id,
          Confirmer_ID: this.accountManager,
          Amount_Of_Compensation: this.computedCompensationAmount,
          Link_To_Course: this.courseLink,
          Course_Name: this.courseName,
          Course_Taking_Reason: this.reason === 'Other' ? this.otherReason : this.reason,
          Comments: this.comments,
          Notification_ID: notificationId,
          Is_USD: (this.currency === 'USD'),
          Payout_Month: formatLocalDate()
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
        this.sendEmailForLAndDRequest(response.data[0].Request_ID)
        this.show = false
        this.$store.dispatch('setSuccess', { successMsg: 'Request for L&D was made' })
      }).catch(error => {
        this.isRequestAppropriate = true
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    createNotification () {
      const isOther = this.reason === 'Other'
      const reason = isOther ? this.otherReason : this.reason
      const compensationText = `${this.computedCompensationAmount} ${this.currency === 'USD' ? '$' : 'uah'}`
      const today = formatLocalDate()

      const notificationData = {
        Notification_Message: `
          Employee: ${this.user.full_name},
          Course link: ${this.courseLink},
          Course name: ${this.courseName},
          Reason: ${reason},
          Amount of compensation: ${compensationText},
          Comments: ${this.comments || '(no comments)'}
        `,
        Notification_Title: 'Request for L&D',
        Notification_Created: today,
        Notification_Employee_ID_From: this.user.employee_id,
        Notification_Employee_ID_To: this.accountManager,
        Notification_Type_ID: 1
      }

      const headers = {
        Authorization: 'Bearer ' + this.user.token
      }

      return this.axios.post('/api/notifications', notificationData, { headers })
    },
    clearAll () {
      this.courseLink = null
      this.courseName = null
      this.searchAM = ''
      this.reason = this.otherReason = 'Personal Development Plan'
      this.accountManager = null
      this.compensationAmount = null
      this.comments = null
    },
    sendEmailForLAndDRequest (requestId) {
      const body = {
        Request_ID: requestId
      }
      this.axios.post('/api/emails/sendemailforlanddrequest', body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
<style>
.inputAmount input[type='number'] {
    -moz-appearance:textfield;
}
.inputAmount input::-webkit-outer-spin-button,
.inputAmount input::-webkit-inner-spin-button {
    -webkit-appearance: none;
}
.tooltip-wrapper {
  position: relative;
}
.l-and-d-info {
  cursor: pointer;
}
.tooltip {
  opacity: 0;
  visibility: hidden;
  position: absolute;
  font-size: 8px;
  top: -30px;
  left: 50%;
  transform: translateX(-50%) translateY(-20px);
  width: 600px;
  padding: 10px;
  background-color: deepskyblue;
}
.tooltip-wrapper:hover .tooltip {
  opacity: 1;
  visibility: visible;
  }
</style>
